import { useCallback, useEffect, useState } from 'react'
import { Button, LinkButton } from '@/atoms/Button'
import { HeartIcon } from '@/atoms/Icons/HeartIcon'
import { CaptionMD, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants'
import { useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { ShareMoment } from '@/organisms/ShareMoment'
import { MomentUserStory } from '@/services/MomentsService/types'
import { useReportUserStoryById, useToggleLikeUserStoryById } from '@/services/StoriesService/hooks'
import { Moment } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { useLoginUrl } from '@/utils/auth-utils'
import { useTranslate } from '@/utils/translate/translate-client'

interface Props {
  story?: MomentUserStory
}

export const StoryActions: React.FC<Props> = ({ story }) => {
  const { isLikedByUser, totalLikes } = story as MomentUserStory & { totalLikes: number }
  const [updateUserLike, setUpdateUserLike] = useState({ isLikedByUser: isLikedByUser, totalLikes: totalLikes })
  const { toggleLikeUserStoryById } = useToggleLikeUserStoryById()
  const { engagementMoment } = useCollectiblesPlayerEngagement()
  const [showReportStoryConfimration, setShowReportStoryConfimration] = useState(false)
  const [showLoginRequired, setShowLoginRequired] = useState(false)
  const { reportUserStoryById } = useReportUserStoryById()
  const { asset } = useAngelPlayerContext()
  const url = `${paths.getBaseURL()}${paths.watch.index}/${asset.projectSlug}/episode/${asset.sourceConfig.guid}`
  const { loginUrl, trackLoginStarted } = useLoginUrl(`${url}?completed=true`)
  const { t } = useTranslate('watch')

  useEffect(() => {
    setUpdateUserLike({ isLikedByUser: isLikedByUser, totalLikes: totalLikes })
  }, [isLikedByUser, totalLikes])

  const [storyReported, setStoryReported] = useState(false)
  const track = useSafeTrack()

  const handleLikeStoryToggle = useCallback(async () => {
    if (!story?.id) return

    try {
      const { data } = await toggleLikeUserStoryById(story.id)

      setUpdateUserLike({
        isLikedByUser: data.toggleLikeUserStory.isLikedByUser,
        totalLikes: data.toggleLikeUserStory.totalLikes,
      })

      track('MOMENT_STORY_LIKE_CLICKED', {
        storyId: story.id,
        isVideo: false,
        isText: true,
      })
    } catch (error) {
      setShowLoginRequired(true)
    }
  }, [story, toggleLikeUserStoryById, track])

  const handleReportingStoryClicked = useCallback(async () => {
    if (!story?.id) return

    await reportUserStoryById(story.id)
    setStoryReported(true)
    track('Report User Story Clicked', {
      storyId: story.id,
    })
  }, [reportUserStoryById, story, track])

  if (!story) return null

  return (
    <div className="flex w-2/3 justify-between">
      <div className="flex items-center">
        <div onClick={handleLikeStoryToggle}>
          <HeartIcon
            className="mr-1 h-5 w-5 cursor-pointer"
            color1="white"
            color2={updateUserLike.isLikedByUser ? 'white' : 'transparent'}
          />
        </div>
        <ParagraphSM>{updateUserLike.totalLikes}</ParagraphSM>
      </div>
      <div className="flex w-[155px] justify-around">
        <ShareMoment moment={engagementMoment as Moment} userId={story.user?.id || ''} story={story} showIcon={false} />
        <Button variant="plain" className="text-md font-light" onClick={() => setShowReportStoryConfimration(true)}>
          {t('report', 'Report')}
        </Button>
      </div>
      {showReportStoryConfimration && (
        <div className="absolute left-1/2 top-0 flex h-full w-[90%] -translate-x-1/2 flex-col justify-center rounded-lg bg-gray-700 py-1">
          <CaptionMD className="my-2 self-center">
            {t('confirmReportingStory', 'Confirm reporting this Story')}
          </CaptionMD>
          <div className="mt-1 flex justify-end pr-4">
            <Button variant="plain" onClick={() => setShowReportStoryConfimration(false)}>
              {t('cancelStorySubmit', 'Cancel')}
            </Button>
            <Button className="mx-3" variant="white" onClick={handleReportingStoryClicked}>
              {t('confirm', 'Confirm')}
            </Button>
          </div>
        </div>
      )}
      {storyReported && (
        <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center rounded-lg bg-gray-700 text-center text-white">
          <CaptionMD>{t('thankYouForReproting', 'Thank you for reporting this story. ')}</CaptionMD>
        </div>
      )}
      {showLoginRequired && (
        <div className="absolute left-1/2 top-0 flex h-full w-[90%] -translate-x-1/2 flex-col justify-center rounded-lg bg-gray-700 py-1">
          <CaptionMD className="my-2 self-center">
            {t('loginRequiredMessage', 'Please login to like this story')}
          </CaptionMD>
          <div className="mt-1 flex justify-end pr-4">
            <Button variant="plain" onClick={() => setShowLoginRequired(false)}>
              {t('cancelStorySubmit', 'Cancel')}
            </Button>
            <LinkButton
              href={loginUrl}
              className="mx-3"
              variant="white"
              onClick={() => trackLoginStarted('collectible-scene-panel')}
            >
              {t('logIn', 'Log In')}
            </LinkButton>
          </div>
        </div>
      )}
    </div>
  )
}
